/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { auth } from '~/api/axios';
import { selectProfiles } from '~/slices/ui';
import useLocalStorage from './useLocalStorage';
import { login as loginQuery } from '~/api/auth';
import { getProfiles } from '~/api/onboarding';
import onError from '~/errorsHandler';
import { SOLUTION_LIST } from '~/env';

export const useCurrentAuthProfileId = () => {
  return useLocalStorage<string | null>(auth.profileLocalStorageKey, null);
};

export const useAllAuthBusinesses = () => {
  const { data, ...rest } = useSelector(selectProfiles);
  return { data: data?.filter((item) => item.type === 'BUSINESS'), ...rest };
};

export const useCurrentAuthBusiness = () => {
  const [currentProfileId] = useCurrentAuthProfileId();
  const { data } = useAllAuthBusinesses();
  const business = data?.find((item) => item.type === 'BUSINESS' && item.id === currentProfileId);
  return business;
};

interface UseLoginProps {
  onSuccess?: () => void;
  onNoBusinessProfile?: () => void;
  onNotAuthorized?: () => void;
}

export const useLogin = (props?: UseLoginProps) => {
  const { onNoBusinessProfile, onNotAuthorized, onSuccess } = props || {};
  const [isLoading, setLoading] = useState(false);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { headers } = await loginQuery(email, password);
      auth.deleteToken();
      auth.setToken(headers, 'password');

      const { data } = await getProfiles();
      const preferredProfileId = auth.getProfile();
      console.log(
        `logging in; checking if any profile is available; preferred profile: ${preferredProfileId}, allowed solutions:`,
        SOLUTION_LIST
      );
      const businessProfiles = data.filter((item) => item.type === 'BUSINESS');
      const availableProfiles = businessProfiles.filter((item) => {
        return SOLUTION_LIST.indexOf(item.solutionId!) >= 0;
      });

      console.log(
        `logging in; found ${availableProfiles.length} available profiles:`,
        availableProfiles
      );

      const businessProfile =
        (preferredProfileId
          ? availableProfiles.find((item) => item.id === preferredProfileId)
          : null) || availableProfiles[0];
      console.log(`logging in; current profile:`, businessProfile);

      if (businessProfile?.id && businessProfile?.solutionId) {
        auth.setProfile(businessProfile.id);
        auth.setSolution(businessProfile.solutionId);
      } else if (businessProfiles.length > 0) {
        throw NotAuthorizedError;
      } else {
        throw NoBusinessError;
      }
      setLoading(false);
      onSuccess?.();
    } catch (err: any) {
      console.log(`err`);

      const error: any = err;
      if (error.message === NO_BUSINESS_PROFILE) {
        onNoBusinessProfile?.();
      } else if (error.message === NOT_AUTHORIZED) {
        console.log(`not authorized`);
        onNotAuthorized?.();
      } else {
        onError(error);
      }
      setLoading(false);
      throw new Error(error);
    }
  };

  return { login, isLoading };
};

const NO_BUSINESS_PROFILE = 'NO_BUSINESS_PROFILE';
const NoBusinessError = new Error(NO_BUSINESS_PROFILE);

const NOT_AUTHORIZED = 'NOT_AUTHORIZED';
const NotAuthorizedError = new Error(NOT_AUTHORIZED);
